import React, { useMemo, useState } from 'react';
import { ArrowRight, IndianRupee, Bike, Navigation, Car, Sparkles } from 'lucide-react';

const CITIES = ['Bangalore', 'Mumbai', 'Delhi NCR', 'Hyderabad', 'Chennai', 'Pune'];

// Base rates per km, per ride type, by city (mocked but realistic)
const BASE_RATE_PER_KM = {
  'Bangalore':  { bike: 7,  auto: 13, mini: 16, sedan: 19, suv: 24 },
  'Mumbai':     { bike: 8,  auto: 15, mini: 18, sedan: 22, suv: 27 },
  'Delhi NCR':  { bike: 7,  auto: 14, mini: 17, sedan: 20, suv: 25 },
  'Hyderabad':  { bike: 6,  auto: 12, mini: 15, sedan: 18, suv: 23 },
  'Chennai':    { bike: 7,  auto: 13, mini: 16, sedan: 19, suv: 24 },
  'Pune':       { bike: 7,  auto: 13, mini: 16, sedan: 19, suv: 24 },
};
const MIN_FARE = { bike: 19, auto: 35, mini: 65, sedan: 85, suv: 120 };

const TYPES = [
  { id: 'bike',  label: 'Bike',     icon: Bike },
  { id: 'auto',  label: 'Auto',     icon: Navigation },
  { id: 'mini',  label: 'OTW Mini', icon: Car },
  { id: 'sedan', label: 'Sedan',    icon: Car },
  { id: 'suv',   label: 'SUV',      icon: Car },
];

export default function FareEstimator() {
  const [city, setCity] = useState('Bangalore');
  const [km, setKm] = useState(8);

  const fares = useMemo(() => {
    const rates = BASE_RATE_PER_KM[city];
    return TYPES.map((t) => {
      const raw = Math.round(rates[t.id] * km + 20); // small booking fee
      const fare = Math.max(raw, MIN_FARE[t.id]);
      const competitor = Math.round(fare * 1.18); // we are ~18% cheaper
      return { ...t, fare, competitor };
    });
  }, [city, km]);

  const cheapest = fares.reduce((a, b) => (a.fare < b.fare ? a : b), fares[0]);

  return (
    <section id="fares" className="bg-white py-24 md:py-32">
      <div className="max-w-[1280px] mx-auto px-5 md:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-[11px] font-semibold tracking-[0.18em] text-[#FF6A1A]">FARE TRANSPARENCY</span>
          <h2 className="mt-3 text-[34px] md:text-[46px] font-semibold tracking-[-0.02em] text-[#0f0f10]">
            Know your fare before you book
          </h2>
          <p className="mt-4 text-[15px] md:text-[16px] text-[#6b6b70] leading-relaxed">
            Real upfront pricing — no surge, no “driver waiting” fee surprises.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-5 gap-6">
          {/* Controls */}
          <div className="lg:col-span-2 bg-[#f7f7f5] rounded-3xl p-7 ring-1 ring-black/[0.04]">
            <div className="text-[10.5px] font-semibold tracking-[0.18em] text-[#6b6b70]">CITY</div>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {CITIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setCity(c)}
                  className={`py-2.5 rounded-xl text-[12.5px] font-semibold transition-colors ${
                    city === c ? 'bg-[#0f0f10] text-white' : 'bg-white text-[#0f0f10] hover:bg-[#eeefe9]'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>

            <div className="mt-7 flex items-center justify-between">
              <div className="text-[10.5px] font-semibold tracking-[0.18em] text-[#6b6b70]">DISTANCE</div>
              <div className="text-[13px] font-semibold text-[#0f0f10]">{km} km</div>
            </div>
            <input
              type="range"
              min={1}
              max={45}
              step={1}
              value={km}
              onChange={(e) => setKm(Number(e.target.value))}
              className="mt-3 w-full accent-[#FF6A1A]"
            />
            <div className="mt-1 flex justify-between text-[10.5px] text-[#6b6b70] font-medium">
              <span>1 km</span><span>15 km</span><span>30 km</span><span>45 km</span>
            </div>

            <div className="mt-8 rounded-2xl bg-white p-4 ring-1 ring-black/[0.04] flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#FFF1E6] text-[#FF6A1A] grid place-items-center">
                <Sparkles size={18} />
              </div>
              <div className="text-[12.5px] text-[#3a3a3d]">
                Cheapest option for <span className="font-semibold text-[#0f0f10]">{km} km in {city}</span> is
                <span className="font-semibold text-[#FF6A1A]"> {cheapest.label}</span> at
                <span className="font-mono font-semibold text-[#0f0f10]"> ₹{cheapest.fare}</span>.
              </div>
            </div>
          </div>

          {/* Fare cards */}
          <div className="lg:col-span-3 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 gap-3">
            {fares.map((f) => {
              const I = f.icon;
              const isCheapest = f.id === cheapest.id;
              return (
                <div
                  key={f.id}
                  className={`rounded-2xl p-5 transition-all relative ${
                    isCheapest
                      ? 'bg-[#0f0f10] text-white ring-2 ring-[#FF6A1A]'
                      : 'bg-[#f7f7f5] text-[#0f0f10] ring-1 ring-black/[0.04] hover:ring-[#FF6A1A]/40'
                  }`}
                >
                  {isCheapest && (
                    <span className="absolute -top-2 right-3 text-[9.5px] font-semibold tracking-[0.16em] bg-[#FF6A1A] text-white px-2 py-1 rounded-full">
                      BEST PRICE
                    </span>
                  )}
                  <div className="flex items-center justify-between">
                    <div className={`w-10 h-10 rounded-xl grid place-items-center ${isCheapest ? 'bg-white/10 text-white' : 'bg-white text-[#0f0f10]'}`}>
                      <I size={18} />
                    </div>
                    <div className={`text-[10.5px] font-semibold tracking-[0.14em] ${isCheapest ? 'text-white/55' : 'text-[#6b6b70]'}`}>
                      ON THE WAY
                    </div>
                  </div>
                  <div className="mt-4 text-[14.5px] font-semibold">{f.label}</div>
                  <div className="mt-2 flex items-baseline gap-1">
                    <IndianRupee size={18} className={isCheapest ? 'text-white' : 'text-[#0f0f10]'} />
                    <span className="text-[28px] md:text-[32px] font-semibold tracking-tight">{f.fare}</span>
                  </div>
                  <div className={`mt-1 text-[11.5px] line-through ${isCheapest ? 'text-white/45' : 'text-[#a3a3a6]'}`}>
                    Others: ₹{f.competitor}
                  </div>
                  <div className={`mt-3 text-[11px] flex items-center gap-1 ${isCheapest ? 'text-[#FFB37A]' : 'text-[#0E8A3F]'} font-semibold`}>
                    You save ₹{f.competitor - f.fare} <ArrowRight size={11} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
